import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { IonHeader, IonToolbar, IonTitle, IonContent } from '@ionic/angular/standalone';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from '@qid/super';
import { OrderStatus } from '../../enum/order-status.enum';
import { OrderPublicApiService, PublicOrderView } from '../../services/order-public-api.service';

/**
 * Public pay page for an admin-generated payment link (`/pay/:token`). No login required.
 * Shows the org/plan/amount, lets the client pay via the Razorpay-hosted page, and polls for
 * completion so it can show a success state once the webhook activates the plan.
 */
@Component({
  selector: 'app-pay-link',
  templateUrl: './pay-link.page.html',
  styleUrls: ['./pay-link.page.scss'],
  standalone: true,
  imports: [CommonModule, SexyModule, SuperButtonComponent, IonHeader, IonToolbar, IonTitle, IonContent],
})
export class PayLinkPage implements OnInit, OnDestroy {

  public token: string;
  public order: PublicOrderView | null = null;
  public loading = true;
  public notFound = false;

  public readonly OrderStatus = OrderStatus;
  private pollHandle: any = null;

  constructor(
    private route: ActivatedRoute,
    private orderPublicApiService: OrderPublicApiService,
  ) {
  }

  async ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token');
    await this.load();
    this.startPolling();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  get isPaid(): boolean {
    return this.order?.orderStatus === OrderStatus.SUBSCRIPTION_OPEN
      || this.order?.orderStatus === OrderStatus.PAID;
  }

  async load() {
    if (!this.token) {
      this.notFound = true;
      this.loading = false;
      return;
    }
    try {
      this.order = await this.orderPublicApiService.getByToken(this.token);
    } catch {
      this.notFound = true;
    } finally {
      this.loading = false;
    }
  }

  payNow() {
    if (this.order?.razorpayShortUrl) {
      window.location.href = this.order.razorpayShortUrl;
    }
  }

  /** Poll for completion (e.g. after the client returns from Razorpay) and stop once paid. */
  private startPolling() {
    this.stopPolling();
    this.pollHandle = setInterval(async () => {
      if (this.isPaid || this.notFound) {
        this.stopPolling();
        return;
      }
      try {
        this.order = await this.orderPublicApiService.getByToken(this.token);
        if (this.isPaid) {
          this.stopPolling();
        }
      } catch {
        // transient; keep polling
      }
    }, 5000);
  }

  private stopPolling() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }
}
